import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, X, Volume2, VolumeX } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { markPushNotificationRead } from "@/lib/rpc/notifications.functions";
import {
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
  playNotificationSound,
} from "@/lib/notification-sound";


type LegacyNotification = {
  id: string;
  source: "legacy";
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
};

type PushItem = {
  id: string;
  source: "push";
  title: string;
  body: string | null;
  link: null;
  read_at: string | null;
  created_at: string;
};

type Item = LegacyNotification | PushItem;

export function NotificationsBell() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    setSoundOn(isNotificationSoundEnabled());
    const handler = () => setSoundOn(isNotificationSoundEnabled());
    window.addEventListener("notification-sound-changed", handler);
    return () => window.removeEventListener("notification-sound-changed", handler);
  }, []);

  const { data: legacy = [] } = useQuery({
    queryKey: ["notifications-legacy", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, body, link, read_at, created_at")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []).map<LegacyNotification>((d) => ({
        id: d.id,
        source: "legacy",
        title: d.title,
        body: d.body,
        link: d.link,
        read_at: d.read_at,
        created_at: d.created_at,
      }));
    },
  });

  const { data: pushes = [] } = useQuery({
    queryKey: ["notifications-push", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("push_notifications")
        .select("id, heading, description, status, created_at")
        .eq("status", "sent")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      const ids = (data ?? []).map((d) => d.id);
      const { data: reads } = ids.length
        ? await supabase
            .from("push_notification_reads")
            .select("notification_id, read_at")
            .eq("user_id", user!.id)
            .in("notification_id", ids)
        : { data: [] as { notification_id: string; read_at: string }[] };
      const readMap = new Map((reads ?? []).map((r) => [r.notification_id, r.read_at]));
      return (data ?? []).map<PushItem>((d) => ({
        id: d.id,
        source: "push",
        title: d.heading,
        body: d.description,
        link: null,
        read_at: readMap.get(d.id) ?? null,
        created_at: d.created_at,
      }));
    },
  });

  const items: Item[] = useMemo(() => {
    return [...legacy, ...pushes].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [legacy, pushes]);

  const unreadCount = items.filter((n) => !n.read_at).length;
  const previousIdsKey = useMemo(
    () => pushes.map((p) => p.id).join("|"),
    [pushes],
  );

  // Toast on new incoming push
  const [seenSet] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    for (const p of pushes) {
      if (!seenSet.has(p.id)) {
        seenSet.add(p.id);
      }
    }
  }, [previousIdsKey, pushes, seenSet]);

  useEffect(() => {
    if (!user?.id) return;

    const ch1 = supabase
      .channel(`notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          playNotificationSound();
          qc.invalidateQueries({ queryKey: ["notifications-legacy", user.id] });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => qc.invalidateQueries({ queryKey: ["notifications-legacy", user.id] }),
      )
      .subscribe();

    const ch2 = supabase
      .channel(`push_notifications:${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "push_notifications" },
        () => {
          qc.invalidateQueries({ queryKey: ["notifications-push", user.id] });
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "push_notifications" },
        () => {
          qc.invalidateQueries({ queryKey: ["notifications-push", user.id] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch1);
      supabase.removeChannel(ch2);
    };
  }, [user?.id, qc]);

  const markRead = async (item: Item) => {
    if (item.source === "legacy") {
      await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", item.id);
      qc.invalidateQueries({ queryKey: ["notifications-legacy", user?.id] });
    } else {
      await markPushNotificationRead({ data: { id: item.id } });
      qc.invalidateQueries({ queryKey: ["notifications-push", user?.id] });
    }
  };

  const markAllRead = async () => {
    const now = new Date().toISOString();
    await supabase.from("notifications").update({ read_at: now }).is("read_at", null);
    const unreadPush = pushes.filter((p) => !p.read_at);
    await Promise.all(
      unreadPush.map((p) => markPushNotificationRead({ data: { id: p.id } })),
    );
    qc.invalidateQueries({ queryKey: ["notifications-legacy", user?.id] });
    qc.invalidateQueries({ queryKey: ["notifications-push", user?.id] });
  };

  const dismiss = async (item: Item) => {
    if (item.source === "legacy") {
      await supabase.from("notifications").delete().eq("id", item.id);
      qc.invalidateQueries({ queryKey: ["notifications-legacy", user?.id] });
    } else {
      // For push, dismissing == marking read
      await markPushNotificationRead({ data: { id: item.id } });
      qc.invalidateQueries({ queryKey: ["notifications-push", user?.id] });
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background hover:bg-muted"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-4 min-w-[16px] rounded-full px-1 text-[9px]"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setNotificationSoundEnabled(!soundOn)}
              aria-label={soundOn ? "Mute notification sound" : "Unmute notification sound"}
              title={soundOn ? "Sound on" : "Sound off"}
              className="rounded p-1 hover:bg-muted"
            >
              {soundOn ? (
                <Volume2 className="h-3.5 w-3.5" />
              ) : (
                <VolumeX className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
                Mark all read
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <p className="p-6 text-center text-xs text-muted-foreground">No notifications</p>
          ) : (
            <ul className="divide-y">
              {items.map((n) => (
                <li
                  key={`${n.source}:${n.id}`}
                  className={`group relative px-3 py-2.5 hover:bg-muted/50 ${!n.read_at ? "bg-primary/5" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                    <div className="min-w-0 flex-1">
                      {n.source === "legacy" && n.link ? (
                        <Link
                          to={n.link}
                          onClick={() => {
                            if (!n.read_at) markRead(n);
                            setOpen(false);
                          }}
                          className="block"
                        >
                          <p className="text-xs font-medium leading-tight">{n.title}</p>
                          {n.body && (
                            <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2">
                              {n.body}
                            </p>
                          )}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            if (!n.read_at) markRead(n);
                          }}
                          className="block w-full text-left"
                        >
                          <p className="text-xs font-medium leading-tight">{n.title}</p>
                          {n.body && (
                            <p className="mt-0.5 text-[11px] text-muted-foreground line-clamp-2">
                              {n.body}
                            </p>
                          )}
                        </button>
                      )}
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col gap-0.5 opacity-0 group-hover:opacity-100">
                      {!n.read_at && (
                        <button
                          onClick={() => markRead(n)}
                          aria-label="Mark read"
                          className="rounded p-0.5 hover:bg-background"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={() => dismiss(n)}
                        aria-label="Dismiss"
                        className="rounded p-0.5 hover:bg-background"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
